import { useTheme } from "@mui/material/styles";
import { OrbitControls, Sphere, Text } from "@react-three/drei";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { useMemo, useRef, useState } from "react";
import useMeasure from "react-use-measure";
import * as THREE from "three";

// 🧠 What contributes to computational expense?
// | Feature                              | Cost Impact            | Notes                                                                          |
// | ------------------------------------ | ---------------------- | ------------------------------------------------------------------------------ |
// | **Canvas / WebGL context**           | 🟡 Moderate            | One canvas is fine; multiple could add load.                                   |
// | **`Sphere` geometry (64 segments)**  | 🔴 High                | `args={[radius, 64, 64]}` creates high-poly spheres.                           |
// | **Real-time `useFrame` updates**     | 🟡 Moderate            | Each `AgentNode` and `ManagerNode` recalculates position every frame (60 FPS). |
// | **`Text` components in 3D**          | 🟡 Moderate            | Text in 3D scenes is more expensive than DOM text.                             |
// | **Stars (`<Stars />` from drei)**    | 🟡 Moderate to 🔴 High | Thousands of particles rendered at once.                                       |
// | **OrbitControls**                    | 🟢 Low                 | Lightweight unless zoom/pan is enabled.                                        |
// | **Lighting (spotLight, pointLight)** | 🟡 Moderate            | Realistic lighting with shadows increases GPU work.                            |

// === 🎨 Color Palette (matching your brand theme) ===
const AGENT_COLOR = "#f97316"; // Auburn
const MANAGER_COLOR = "#000080"; // Yale Blue
const SPECULAR_COLOR = "#B9D9EB"; // Columbia Blue
const TEXT_COLOR = "#FFFFFF";

const AGENT_LABELS: { title: string; subtitle: string }[] = [
  { title: "Customer Support", subtitle: "For external customer inquiries" },
  { title: "Planner / PM", subtitle: "Manages tasks and coordinates progress" },
  {
    title: "Operations",
    subtitle: "Executes daily workflows and system tasks",
  },
  {
    title: "Information Desk",
    subtitle: "Answers questions from internal data and tools",
  },
  { title: "Communications", subtitle: "Email bot with real email address" },
  {
    title: "Data Analyst",
    subtitle: "Finds insights and KPIs in your business data",
  },
  { title: "Content Creator", subtitle: "Creates marketing and brand content" },
];

// ─────────────────────────────────────────────────────
// 🌌 Scene background (currently not used, but can be enabled)
// ─────────────────────────────────────────────────────
function SpaceBackground() {
  const texture = useLoader(THREE.TextureLoader, "/textures/background_2.jpg");
  return (
    <mesh scale={[-100, 100, 100]}>
      <sphereGeometry args={[1, 32, 32]} />
      <meshBasicMaterial map={texture} side={THREE.BackSide} />
    </mesh>
  );
}

// ─────────────────────────────────────────────────────
// Functionality to split text lines
// ─────────────────────────────────────────────────────
function wrapText(str: string, maxLen: number): string {
  const words = str.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    if ((currentLine + word).length <= maxLen) {
      currentLine += (currentLine ? " " : "") + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines.join("\n");
}

// ─────────────────────────────────────────────────────
// 🪐 AgentNode: orbiting smaller sphere with label
// ─────────────────────────────────────────────────────
function AgentNode({
  title,
  subtitle,
  index,
  scrollSpeed,
  radius = 20,
  baseSpeed = 0.2,
  scale = 1,
  yOffset = 4,
  agentRadius = 0.25,
  agentCount = 6,
  textFacesCamera = false,
  motionMode = "constant",
}: {
  title: string;
  subtitle: string;
  index: number;
  scrollSpeed: number;
  radius?: number;
  baseSpeed?: number;
  scale?: number;
  yOffset?: number;
  agentRadius?: number;
  agentCount?: number;
  textFacesCamera?: boolean;
  motionMode?: "constant" | "attentionBased";
}) {
  const theme = useTheme();
  const { custom } = theme.palette;

  const sphereRef = useRef<THREE.Mesh>(null);
  const textRef = useRef<THREE.Mesh>(null);
  const textRef2 = useRef<THREE.Mesh>(null);
  const wrappedSubtitle = useMemo(() => wrapText(subtitle, 30), [subtitle]);
  const subtitleLineCount = useMemo(
    () => wrappedSubtitle.split("\n").length,
    [wrappedSubtitle]
  );
  const [visible, setVisible] = useState(0); // Opacity from 0 to 1

  const [localSpeed, setLocalSpeed] = useState(baseSpeed);
  const [focusTime, setFocusTime] = useState(0); // seconds in focus

  // 🧮 Spacing (tweak as needed)
  const titleFontSize = 0.16 * scale;
  const subtitleFontSize = 0.1 * scale;
  const subtitleLineHeight = 1.2; // line spacing multiplier
  const spacingBetweenTexts = 0.04 * scale; // spacing between title and subtitle

  // 🟢 1. Title goes below sphere at a safe distance
  const titleYOffset = -agentRadius - 0.35 * scale;

  // 🔵 2. Subtitle goes below title, based on number of lines
  const subtitleYOffset =
    titleYOffset -
    subtitleFontSize * subtitleLineCount * subtitleLineHeight -
    spacingBetweenTexts;

  useFrame(({ clock, camera }) => {
    const t = clock.getElapsedTime();

    const cameraDir = new THREE.Vector3();
    camera.getWorldDirection(cameraDir);
    cameraDir.y = 0; // Flatten for horizontal check

    const angleOffset = index * ((Math.PI * 2) / agentCount);
    const angle = t * localSpeed + angleOffset;

    const x = radius * Math.cos(angle);
    const z = radius * Math.sin(angle);
    const y = yOffset;

    const position = new THREE.Vector3(x, y, z);
    const directionToCamera = new THREE.Vector3()
      .subVectors(camera.position, position)
      .normalize();
    directionToCamera.y = 0;

    const dot = cameraDir.dot(directionToCamera); // -1 (back) to 1 (front)
    const isCentered = dot > 0.2; // ~within ~80 cone

    // Compute frontness in XZ-plane:
    // agent direction from origin
    const agent2D = new THREE.Vector2(x, z).normalize();
    // camera direction from origin
    const cam2D = new THREE.Vector2(
      camera.position.x,
      camera.position.z
    ).normalize();

    const frontDot = agent2D.dot(cam2D); // -1..1  ( >0 = facing viewer )

    // Map -1..1 to 0..1 with a gentle bias so it’s visible longer
    const targetOpacity = THREE.MathUtils.clamp((frontDot + 0.2) / 0.8, 0, 1);

    setVisible((prev) => THREE.MathUtils.lerp(prev, targetOpacity, 0.25));

    if (motionMode === "attentionBased") {
      if (isCentered) {
        setFocusTime((prev) => Math.min(prev + 1 / 60, 5));
      } else {
        setFocusTime(0);
      }

      const slowedSpeed = baseSpeed * 0.25;
      const fastSpeed = baseSpeed * 1.8;
      const ease = THREE.MathUtils.smoothstep(focusTime / 5, 0, 1);
      const targetSpeed = slowedSpeed * ease + fastSpeed * (1 - ease);

      setLocalSpeed((prev) => THREE.MathUtils.lerp(prev, targetSpeed, 0.05));
    }

    if (sphereRef.current) sphereRef.current.position.set(x, y, z);
    if (textRef.current) {
      textRef.current.position.set(x, y + titleYOffset, z);
      textFacesCamera
        ? textRef.current.lookAt(camera.position)
        : (textRef.current.rotation.y = Math.atan2(x, z));
    }
    if (textRef2.current) {
      textRef2.current.position.set(x, y + subtitleYOffset, z);
      textFacesCamera
        ? textRef2.current.lookAt(camera.position)
        : (textRef2.current.rotation.y = Math.atan2(x, z));
    }

    // dot = 1 → agent in front of camera
    // dot = 0 → agent at 90°
    // dot = -1 → agent behind
    // const targetOpacity = dot > 0 ? 1 : 0;
    // setVisible((prev) => THREE.MathUtils.lerp(prev, targetOpacity, 0.1));

    // setVisible(1);
  });

  return (
    <>
      <Sphere args={[agentRadius * scale, 16, 16]} ref={sphereRef}>
        <meshPhongMaterial
          color={custom.accent}
          specular={custom.accent}
          emissive={custom.accent} // ← makes it glow slightly
          emissiveIntensity={0.14} // ← tweak this it was 0.4
          shininess={1} // ← glossy effect
        />
      </Sphere>

      {/* TITLE (Bold) */}
      <Text
        ref={textRef}
        fontSize={0.16 * scale}
        color={TEXT_COLOR}
        anchorX="center"
        anchorY="bottom"
        fillOpacity={visible}
        material-toneMapped={false}
      >
        {title}
      </Text>

      {/* SUBTITLE (Smaller, below the title) */}
      <Text
        ref={textRef2}
        fontSize={0.1 * scale}
        color={TEXT_COLOR}
        anchorX="center"
        anchorY="bottom"
        fillOpacity={visible}
        material-toneMapped={false}
      >
        {wrappedSubtitle}
      </Text>
    </>
  );
}

// ─────────────────────────────────────────────────────
// 🌍 ManagerNode: large central sphere with label
// ─────────────────────────────────────────────────────
function ManagerNode({
  scale = 1,
  scrollSpeed = 0,
  radius = 2,
}: {
  scale?: number;
  scrollSpeed?: number;
  radius?: number;
}) {
  const theme = useTheme();
  const { custom } = theme.palette;

  const ref = useRef<THREE.Mesh>(null);
  const titleRef = useRef<THREE.Mesh>(null);
  const subtitleRef = useRef<THREE.Mesh>(null);

  // Define subtitle
  const subtitle = "That  by you)";
  const subtitleFontSize = 0.2 * scale;
  const titleFontSize = 0.18 * scale * 2;
  const spacingBetweenTexts = 0.05 * scale;

  // 🧮 Calculate vertical offsets
  const titleYOffset = radius * 1.1 * scale;
  const subtitleYOffset =
    titleYOffset + subtitleFontSize * 1.2 + spacingBetweenTexts;

  useFrame(({ clock, camera }) => {
    const t = clock.getElapsedTime();
    const angle = t * 0.4;

    // 🔄 Rotate the manager
    if (ref.current) {
      ref.current.rotation.y = angle;
    }

    // 🏷️ Orient both texts toward camera (Y-axis billboard)
    if (titleRef.current) {
      titleRef.current.position.set(0, titleYOffset, 0);
      titleRef.current.lookAt(
        new THREE.Vector3(camera.position.x, titleYOffset, camera.position.z)
      );
    }

    if (subtitleRef.current) {
      subtitleRef.current.position.set(0, subtitleYOffset - titleFontSize, 0);
      subtitleRef.current.lookAt(
        new THREE.Vector3(camera.position.x, subtitleYOffset, camera.position.z)
      );
    }
  });

  return (
    <>
      {/* <Sphere ref={ref} args={[radius * scale, 32, 32]}> */}
      <Sphere ref={ref} args={[radius * scale, 32, 32]}>
        <meshPhongMaterial
          color={custom.primaryDark} // Yale Blue
          shininess={1}
          specular={custom.primaryDark} // Payne’s Gray or Columbia Blue
          emissive={custom.primaryDark} // ← makes it glow slightly
          emissiveIntensity={0.15} // ← tweak this it was 0.4
        />
      </Sphere>

      {/* === TITLE === */}
      {/* <Text
        characters="ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz "
        ref={titleRef}
        fontSize={titleFontSize}
        color={TEXT_COLOR}
        anchorX="center"
        anchorY="bottom"
        material-toneMapped={false}
      >
        AI Manager
      </Text> */}

      {/* === SUBTITLE === */}
      {/* <Text
        ref={subtitleRef}
        fontSize={subtitleFontSize}
        color={TEXT_COLOR}
        anchorX="center"
        anchorY="bottom"
        material-toneMapped={false}
      >
        {subtitle}
      </Text> */}
    </>
  );
}

function OrbitingManagerLabel({
  text = "AI MANAGER",
  radius = 4.5,
  segments = 12,
}: {
  text?: string;
  radius?: number;
  segments?: number;
}) {
  const groupRef = useRef<THREE.Group>(null);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    if (groupRef.current) {
      groupRef.current.rotation.y = t * 0.2;
      groupRef.current.rotation.x = Math.sin(t * 0.2) * 0.2; // wobble
    }
  });

  const letters = text.split("");
  return (
    <group ref={groupRef}>
      {letters.map((char, i) => {
        const angle = (i / letters.length) * Math.PI * 2;
        const x = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;
        const rotationY = -angle + Math.PI;

        return (
          <Text
            characters="ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz "
            key={i}
            position={[x, 4.2, z]} // Adjust Y to float over manager
            rotation={[0, rotationY, 0]}
            fontSize={0.2}
            color="#FFFFFF"
            material-toneMapped={false}
          >
            {char}
          </Text>
        );
      })}
    </group>
  );
}

// ─────────────────────────────────────────────────────
// 🎬 Main Scene: lights, orbits, agents, manager
// ─────────────────────────────────────────────────────
function Scene({
  sizeRatio,
  scaleFactor,
  scrollSpeed,
  managerRadius = 3,
  orbitRadius = 4,
  agentRadius = 0.25,
}: {
  sizeRatio: number;
  scaleFactor: number;
  scrollSpeed: number;
  managerRadius?: number;
  orbitRadius?: number;
  agentRadius?: number;
}) {
  const agentCount = 8;

  const agents = useMemo(() => {
    return AGENT_LABELS.map((agent, i) => {
      const angle = (i / AGENT_LABELS.length) * Math.PI * 2;
      const x = Math.cos(angle) * orbitRadius;
      const z = Math.sin(angle) * orbitRadius;
      return {
        title: agent.title,
        subtitle: agent.subtitle,
        index: i,
        position: [x, 0, z] as [number, number, number],
      };
    });
  }, [orbitRadius]);

  return (
    <>
      {/* Optional: enable this if you want the sphere-mapped background */}
      {/* <SpaceBackground /> */}

      {/* 🌌 Background stars */}
      {/* <Stars radius={100} depth={50} count={5000} factor={4} fade /> */}

      {/* 💡 Lighting setup */}
      <ambientLight intensity={0.3} />
      <directionalLight position={[0, 10, 0]} intensity={1.5} />
      <pointLight position={[5, 5, 5]} intensity={0.6} />
      <spotLight
        position={[0, 10, -10]}
        angle={0.5}
        penumbra={0.4}
        intensity={0.8}
      />

      <OrbitControls
        enableRotate={false}
        enableZoom={false}
        enablePan={false}
      />

      {/* 🎯 Agent + Manager system */}
      {/* <group
        position={[0, 0, 0]}
        scale={[scaleFactor, scaleFactor, scaleFactor]}
        rotation={[Math.PI / 6, 0, 0]} // Tilt 30° forward (X-axis)
      > */}

      <group
        position={[0, 0, 0]}
        scale={[scaleFactor, scaleFactor, scaleFactor]}
      >
        <ManagerNode
          scale={sizeRatio}
          scrollSpeed={scrollSpeed}
          radius={managerRadius} // ✅ Manager size
        />
        {/* <OrbitingManagerLabel text="STAFFBOTICS AI MANAGER" radius={10} /> */}

        {agents.map((agent) => (
          <AgentNode
            key={agent.index}
            title={agent.title}
            subtitle={agent.subtitle}
            index={agent.index}
            scrollSpeed={scrollSpeed}
            radius={orbitRadius}
            scale={sizeRatio}
            agentRadius={agentRadius}
            yOffset={4}
            agentCount={agents.length}
            textFacesCamera={true}
            motionMode="constant"
          />
        ))}
      </group>
    </>
  );
}

// ─────────────────────────────────────────────────────
// 🎥 Main Component: Responsive canvas with tracking
// ─────────────────────────────────────────────────────
export default function Agent({
  sizeRatio = 2,
  scrollSpeed,
}: {
  sizeRatio?: number;
  scrollSpeed: number;
}) {
  const [ref, bounds] = useMeasure();

  const managerRadius = 3.75;
  const orbitRadius = managerRadius * 2 + 1;
  const agentRadius = 0.2;
  const cameraDistance = 10; // same as Canvas camera.position.z
  const fov = 60; // degrees

  const scaleFactor = useMemo(() => {
    if (!bounds.width || !bounds.height) return 1;

    const width = bounds.width;
    const height = bounds.height;
    const aspect = width / height;

    // --- 1. Required horizontal & vertical diameter of scene
    const sceneRadius = orbitRadius + agentRadius;
    const sceneHeightNeeded = (managerRadius + 1) * sizeRatio * 2; // for top label
    const sceneWidthNeeded = sceneRadius * 2;

    // --- 2. Visible height at given distance & FOV
    const fovRad = (fov * Math.PI) / 180;
    const visibleHeight = 2 * Math.tan(fovRad / 2) * cameraDistance;
    const visibleWidth = visibleHeight * aspect;

    let scale = 1;
    if (sceneHeightNeeded > visibleHeight) {
      scale *= visibleHeight / sceneHeightNeeded;
    }
    if (sceneWidthNeeded > visibleWidth) {
      scale *= visibleWidth / sceneWidthNeeded;
    }

    // Optional further reduction for very small containers
    if (width < 400) scale *= 0.9;
    if (height < 400) scale *= 0.9;

    return scale;
  }, [bounds.width, bounds.height]);

  return (
    <div
      ref={ref}
      style={{
        width: "100%",
        height: "100%",
        overflow: "visible", // ⚠️ let canvas overflow if needed
        position: "relative",
      }}
    >
      <Canvas
        camera={{ position: [0, 4, cameraDistance], fov }}
        style={{ overflow: "visible", position: "relative" }}
      >
        <Scene
          sizeRatio={sizeRatio}
          scaleFactor={scaleFactor}
          scrollSpeed={scrollSpeed}
          managerRadius={managerRadius}
          orbitRadius={orbitRadius}
          agentRadius={agentRadius}
        />
      </Canvas>
    </div>
  );
}
